/**
 * Typed fetch wrappers for the EpubJs transport endpoints added in
 * Phase 1a (spine) + Phase 1b (manifest). All calls go through `apiClient`
 * so the axios OIDC-Bearer-token interceptor applies.
 */
import { apiClient } from './apiClient';

const BOOKS_EXTN_API = '/api/app/books-extn';

export interface EpubJsSpineItemManifestDto {
  spineIndex: number;
  href: string;
  mediaType: string;
  /**
   * Id of the underlying SecureContentChunk row for this spine item.
   * Used by the Flashcard Library tab to resolve the currently-viewed
   * spine to a concrete chunk Guid (without issuing a /search-in-book call).
   */
  secureContentChunkId: string;
}

export interface EpubJsTocItemDto {
  label: string;
  href: string;
  children: EpubJsTocItemDto[];
}

export interface EpubJsManifestDto {
  packageTitle: string;
  packageAuthor: string | null;
  packageLanguage: string;
  packageIdentifier: string;
  spineItems: EpubJsSpineItemManifestDto[];
  tocItems: EpubJsTocItemDto[];
}

export interface SpineItemDto {
  spineIndex: number;
  spineItemHref: string;
  xhtmlContent: string;
  totalSpineItems: number;
}

export const epubjsService = {
  /**
   * One-shot call at book-open time. The custom archive loader uses the
   * result to synthesize container.xml and content.opf.
   */
  async getManifest(bookId: string): Promise<EpubJsManifestDto> {
    const response = await apiClient.get<EpubJsManifestDto>(
      `${BOOKS_EXTN_API}/epubjs/manifest/${bookId}`
    );
    return response.data;
  },

  /**
   * Called lazily by the custom archive loader for each spine item the
   * reader opens. Returns already-decrypted XHTML.
   */
  async getSpineItem(bookId: string, spineIndex: number): Promise<SpineItemDto> {
    const response = await apiClient.get<SpineItemDto>(
      `${BOOKS_EXTN_API}/epubjs/spine/${bookId}/${spineIndex}`
    );
    return response.data;
  },

  /**
   * Fetches a raw asset (image/css/font) as ArrayBuffer for epub.js's
   * `replacements: "blobUrl"` path. The backend endpoints take `?path=` —
   * we URL-encode the original manifest href here. Kind = "image" | "css" | "font".
   */
  async getAssetBytes(kind: 'image' | 'css' | 'font', path: string): Promise<ArrayBuffer> {
    const response = await apiClient.get<ArrayBuffer>(
      `${BOOKS_EXTN_API}/${kind}`,
      {
        params: { path },
        responseType: 'arraybuffer',
      }
    );
    return response.data;
  },
};
